import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authApiV2 } from '@/api/endpoints/auth';
import { AuthMarketingShell } from '@/features/auth/components/AuthMarketingShell';
import { AuthTextField } from '@/features/auth/components/AuthTextField';
import { DEFAULT_POST_AUTH_PATH } from '@/lib/default-landing';

/**
 * /mfa/enroll — Generate a TOTP secret, show the QR code, and confirm a
 * verification code before enabling MFA on the account.
 */
export function MfaEnrollPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const enrollQuery = useQuery({
    queryKey: ['auth', 'mfa', 'enroll'],
    queryFn: () => authApiV2.mfaEnroll(),
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    setVerifyError(null);
  }, [code]);

  const verify = useMutation({
    mutationFn: (c: string) =>
      authApiV2.mfaVerify({ code: c, mfaToken: enrollQuery.data?.secret ?? '' }),
    onSuccess: () => {
      navigate(DEFAULT_POST_AUTH_PATH, { replace: true });
    },
    onError: () => {
      setVerifyError('That code didn\'t match. Double-check your authenticator and try again.');
    },
  });

  function onVerify(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    verify.mutate(code);
  }

  if (enrollQuery.isLoading) {
    return <AuthMarketingShell isLoading />;
  }
  if (enrollQuery.isError || !enrollQuery.data) {
    return (
      <AuthMarketingShell
        title="Unable to start MFA enrollment"
        subtitle="Please try again in a moment."
      >
        <button
          type="button"
          onClick={() => enrollQuery.refetch()}
          className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Try again
        </button>
      </AuthMarketingShell>
    );
  }

  const { qrCode, secret } = enrollQuery.data;

  return (
    <AuthMarketingShell
      title="Set up two-factor authentication"
      subtitle="Scan this QR code with Authy, Google Authenticator, or 1Password, then enter the 6-digit code it shows to confirm."
    >
      {/*
        Literal white, in both themes, on purpose: the QR is a black-on-
        transparent bitmap and phone cameras need the light quiet zone to
        read it. This is scannable canvas, not chrome — do not tokenise it.
      */}
      <div className="mb-4 flex justify-center rounded-md bg-white p-4">
        {/* qrCode is a data: URL returned from speakeasy qrcode-image */}
        <img src={qrCode} alt="MFA QR code" className="h-48 w-48" width={192} height={192} />
      </div>

      <p
        className="mb-5 break-all rounded-md bg-muted px-3 py-2 text-center font-mono text-xs text-foreground"
        data-clarity-mask="true"
      >
        {secret}
      </p>

      <form onSubmit={onVerify} autoComplete="off" className="flex flex-col gap-3">
        {verifyError && (
          <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive-text">
            {verifyError}
          </div>
        )}
        <AuthTextField
          name="one-time-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          onClear={() => setCode('')}
          clearLabel="Clear verification code"
          placeholder="123456"
          inputClassName="text-center font-mono text-lg tracking-[0.5em]"
          autoFocus
        />
        <button
          type="submit"
          disabled={code.length !== 6 || verify.isPending}
          className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {verify.isPending ? 'Verifying…' : 'Enable 2FA'}
        </button>
      </form>
    </AuthMarketingShell>
  );
}
